<template>
  <!-- Error Message -->
  <PageError v-if="error" :error="error" :errorCode="errorCode" />

  <div v-else-if="canView || user?.is_staff" class="dark:text-white">

  <div v-if="changingUser"
      class="flex items-center justify-center inset-0 fixed bg-black/50 backdrop-blur-sm z-40"
      @click.self="changingUser = false"
    >
    <div class="p-6 glass-card mb-2 w-[80%] max-w-md min-h-[35%] shadow-xl z-50 flex flex-col justify-between">

      <!-- Header -->
      <div>

        <h2 class="text-lg font-semibold mb-1">Change User Role</h2>
        <p class="text-sm mb-6">
          Changing role from <span class="text-amber-400 font-medium">"{{ currentRole }}"</span> to a new role.
        </p>

        <!-- Select -->
        <div class="mb-4">
          <label class="block text-xs uppercase tracking-widest mb-2">Select Role</label>
          <div class="relative">
            <GlassSelect
              v-model="selectedRole"
              :options="[{id:'user',name:'User'},{id:'admin',name:'Admin'}]"
              placeholder="Select a role"
            />
          </div>

          <!-- Badge preview -->
          <div class="mt-3 flex items-center gap-2">
            <span class="text-xs">Preview:</span>
            <span
              :class="selectedRole === 'admin'
                ? 'bg-amber-500/20 text-amber-400 border border-amber-500/30'
                : 'bg-cyan-500/20 text-cyan-400 border border-cyan-500/30'"
              class="px-3 py-0.5 rounded-full text-xs font-semibold tracking-wide transition-all"
            >
              {{ selectedRole.toUpperCase() }}
            </span>
          </div>
        </div>
      </div>

      <!-- Divider + Actions -->
      <div>
        <div class="h-px bg-white/10 mb-5" />
        <div class="flex gap-3 justify-end">
          <button
            @click="changingUser = false"
            class="btn text-sm font-medium transition-all"
          >
            Cancel
          </button>
          <button
            @click="changeUserRole"
            class="btn text-sm font-semibold hover:opacity-90 active:scale-95 transition-all"
          >
            Save Changes
          </button>
        </div>
      </div>

    </div>
  </div>


  <div v-if="addingUser"
      class="flex items-center justify-center inset-0 fixed bg-black/50 backdrop-blur-sm z-40"
      @click.self="addingUser = false"
    >
    <div class="p-6 glass-card mb-2 w-[80%] max-w-md min-h-[35%] shadow-xl z-50 flex flex-col justify-between">

      <!-- Header -->
      <div>

        <h2 class="text-lg font-semibold mb-1">Add New User</h2>

        <div class="space-y-4">
         
          <div>
            <label class="block text-xs uppercase tracking-widest mb-2">User Email Address</label>
            <input 
              type="text"
              v-model="newUserObject.email"
              :class="[
                'block w-full px-3 py-2 text-base border focus:outline-none focus:ring-2 sm:text-sm rounded-md',
                fieldErrors['email']
                  ? 'ring-2 ring-red-500 border-red-500 focus:ring-red-500 focus:border-red-500'
                  : 'border-gray-300 focus:ring-blue-500 focus:border-blue-500'
              ]"
            >
          </div>

          <div>
            <label class="block text-xs uppercase tracking-widest mb-2">User Password</label>
            <input 
              type="password"
              v-model="newUserObject.password"
              :class="[
                  'block w-full px-3 py-2 text-base border focus:outline-none focus:ring-2 sm:text-sm rounded-md',
                  fieldErrors['password']
                    ? 'ring-2 ring-red-500 border-red-500 focus:ring-red-500 focus:border-red-500'
                    : 'border-gray-300 focus:ring-blue-500 focus:border-blue-500'
                ]"
            >
          </div>
        </div>
      </div>

      <!-- Divider + Actions -->
      <div>
        <div class="h-px bg-white/10 mb-5" />
        <div class="flex gap-3 justify-end">
          <button
            @click="addingUser = false"
            class="btn text-sm font-medium text-white/60 hover:text-white transition-all"
          >
            Cancel
          </button>
          <button
            @click="addANewUser"
            class="btn text-sm font-semibold text-white hover:opacity-90 active:scale-95 transition-all"
          >
            Save Changes
          </button>
        </div>
      </div>

    </div>
  </div>


  <div v-if="isTogglingUserStatus"
      class="flex items-center justify-center inset-0 fixed bg-black/50 backdrop-blur-sm z-40"
      @click.self="isTogglingUserStatus = false"
    >
    <div class="p-6 glass-card mb-2 w-[80%] max-w-md min-h-[35%] shadow-xl z-50 flex flex-col justify-between">
      <div>
        <h2 class="text-lg font-semibold mb-1">{{ pendingUser?.is_active ? 'Deactivate' : 'Activate' }}: {{ pendingUser.email }}</h2>

        <div class="space-y-4">
          <div>
            <p class="text-sm mb-6">
               Are you sure you want to {{  pendingUser?.is_active ? 'deactivate' : 'activate' }} this user.
            </p>
          </div>
        </div>
      </div>

      <div>
        <div class="h-px bg-white/10 mb-5" />
        <div class="flex gap-3 justify-end">
          <button
            @click="pendingUser?.is_active ? deactivateThisUser() : activateThisUser()"
            class="px-5 py-2.5 rounded-xl text-sm font-medium transition-all"
          >
            Yes
          </button>
          <button
             @click="isTogglingUserStatus = false"
            class="btn text-sm font-semibold text-white hover:opacity-90 active:scale-95 transition-all"
          >
            No
          </button>
        </div>
      </div>
    </div>
  </div>
  
    <!-- Header Section -->
    <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
      <h1 class="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
        <svg xmlns="http://www.w3.org/2000/svg" class="w-7 h-7 sm:w-8 sm:h-8 bg-white/50 rounded px-1" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
            <path stroke-linecap="round" stroke-linejoin="round" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
        </svg>
        User Management
        <span v-if="isFiltered" class="text-sm text-gray-500">(filtered)</span>
      </h1>

      <div class="flex space-x-3">
        <button
          type="button"
          class="hover:bg-transparent bg-transparent"
          @click="showHowToDevice = true"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="dark:text-gray-50 text-gray-900">
            <circle cx="12" cy="12" r="10"/>
            <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/>
            <path d="M12 17h.01"/>
          </svg>
        </button>
        <button
          @click="fetchDevices"
          class="btn bg-gray-700 text-white hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-gray-500"
        >
          Refresh
        </button>
      </div>
    </div>

    <!-- Search and Filter Section -->
    <div class="flex gap-4 mb-6">
      <!-- Search Bar -->
      <div class="w-full">
        <SearchBar
          v-model="searchQuery"
          placeholderText="Search users..."
        />
      </div>

      <!-- Bulk Action Section -->
      <div class="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
        <GlassSelect
          v-model="bulkAction"
          :options="(canDelete || user?.is_staff) ? [{id:'delete',name:'Delete selected items'}] : []"
          placeholder="Bulk Actions"
        />
        <button
          @click="applyBulkAction"
          :disabled="!bulkAction || selectedUsers.length === 0"
          :class="[
            'w-full sm:w-auto btn focus:outline-none focus:ring-2 focus:ring-blue-500 text-base',
            bulkAction && selectedUsers.length
              ? 'bg-red-600 text-white hover:bg-red-700'
              : 'bg-gray-300 text-gray-500 cursor-not-allowed',
          ]"
        >
          Apply
        </button>
      </div>
    </div>

    <!-- Devices Table -->
    <GenericTable
      ref="table"
      :columns="columns"
      :items="paginatedUsers"
      itemKey="id"
      :selectable="true"
      @update:selectedItems="updateSelectedUsers"
    >
      <!-- Custom cell templates -->
       <template #role="{ value }">
        <span
          class="px-3 py-2 rounded-full text-xs font-medium bg-yellow-600 text-white dark:bg-yellow-500/50 dark:text-yellow-500 uppercase"
        >
          {{ value }}
        </span>
      </template>
      <template #is_active="{ value }">
        <span
          :class="[
            'px-3 py-2 rounded-full text-xs font-medium',
            value ? 'bg-green-700 text-white dark:bg-green-500/20 dark:text-green-500' : 'bg-gray-500 text-white dark:bg-gray-500/20 dark:text-gray-500'
          ]"
        >
          {{ value ? 'ACTIVE' : 'INACTIVE' }}
        </span>
      </template>
      <template #mac_address="{ value }">
        <span class="text-cyan-400">{{ value }}</span>
      </template>
      <template #created_at="{ value }">
        {{ formatValue("created_at", value) }}
      </template>
      <template #actions="{ item }">
        <div class="flex gap-3 p-4">

          <button v-if="!item.is_active"
              class="flex items-center gap-2 btn text-black bg-green-600 border border-slate-600 hover:bg-green-700 hover:border-slate-500 active:scale-98 transition-all"
              type="button"
              @click.stop="manageUserStatus(item)"
              >
              <EyeIcon/>
              <span class="text-sm font-medium">Activate</span>
            </button>


            <button v-if="item.is_active"
                class="flex items-center gap-2 btn text-black bg-yellow-600 bg-transparent border border-slate-600 hover:bg-yellow-700 hover:border-slate-500 active:scale-98 transition-all"
                type="button"
                @click.stop="manageUserStatus(item)"
                >
                <EyeClosed/>
                <span class="text-sm font-medium">Deactivate</span>
            </button>
            
            <button 
                class="flex items-center gap-2 btn border border-red-600 text-white hover:bg-slate-700 hover:border-red-700 active:scale-98 transition-all"
                type="button"
                @click.stop="manageRole(item)"
                >
                <User2Icon/>
                <span class="text-sm font-medium">Change Role</span>
            </button>

            <button 
                class="flex items-center gap-2 btn bg-red-600 border border-red-600 text-white hover:bg-red-700 hover:border-red-700 active:scale-98 transition-all"
                type="button"
                @click.stop="deleleThisUser(item.id)"
                >
                <Trash2/>
                <span class="text-sm font-medium">Remove</span>
            </button>
        </div>
      </template>
    </GenericTable>

    <!-- Pagination -->
    <div class="flex flex-col gap-4 mt-8">
      <div class="flex flex-col sm:flex-row items-center justify-between gap-4">
        <div class="flex items-center gap-2 w-full sm:w-auto">
          <button
            @click="changePage(-1)"
            :disabled="currentPage === 1"
            class="flex-1 sm:flex-initial px-6 py-3 rounded-lg bg-gray-800 text-gray-100 hover:bg-gray-900 disabled:opacity-50 disabled:cursor-not-allowed transition duration-200 text-base"
          >
            Previous
          </button>
          <button
            @click="changePage(1)"
            :disabled="currentPage === totalPages"
            class="flex-1 sm:flex-initial px-6 py-3 rounded-lg bg-gray-800 text-gray-100 hover:bg-gray-900 disabled:opacity-50 disabled:cursor-not-allowed transition duration-200 text-base"
          >
            Next
          </button>
        </div>

        <div class="text-gray-700 dark:text-white text-center">
          Page {{ currentPage }} of {{ totalPages }}
        </div>

        <div class="w-full sm:w-auto">
          <GlassSelect
            v-model="itemsPerPage"
            :options="[{id:10,name:'10 per page'},{id:20,name:'20 per page'},{id:50,name:'50 per page'},{id:100,name:'100 per page'},{id:-1,name:'All'}]"
            placeholder="Items per page"
            @change="updatePagination"
          />
        </div>
      </div>
    </div>

    <DeleteModal
      v-if="deletingItem"
      :deleting-item="deletingItem"
      :componet-objects="componetObjects"
      :is-deleting="isDeleting"
      @delete="confirmDelete"
      @close="deletingItem = false"
    />
  </div>
</template>


<script setup>
import { ref, computed, watch, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import SearchBar from '@/components/SearchBar.vue';
import GenericTable from '@/components/GenericTable.vue';
import PageError from '@/components/PageError.vue';
import DeleteModal from '@/components/DeleteModal.vue';
import {
  getUsers,
  activateUser,
  deactivateUser,
  updateUserRole,
  deleteUser,
  addNewUser
} from '@/services/mivulite/usersService';
import { useStore } from '@/store';
import { Eye, EyeClosed, EyeIcon, Trash2, User2Icon } from 'lucide-vue-next';
import GlassSelect from '@/components/GlassSelect.vue';

const router = useRouter();

// Refs
const showHowToDevice = ref(false);
const users = ref([]);
const searchQuery = ref("");
const filteredUsers = ref([]);
const currentPage = ref(1);
const itemsPerPage = ref(20);
const selectedUsers = ref([]);
const deletingItem = ref(false);
const bulkAction = ref("");
const canAdd = ref(false);
const canDelete = ref(false);
const canView = ref(false);
const error = ref("");
const errorCode = ref("");
const isDeleting = ref(false);
const table = ref(null);
const changingUser = ref(false)
const selectedRole = ref('')
const currentRole = ref('')
const addingUser = ref('')
const isTogglingUserStatus = ref(false)
const pendingUserID = ref('')
const pendingUser = ref('')
const newUserObject = ref({
  email: '',
  password: ''
})
const fieldErrors = ref({})

// Constants
const columns = [
  { key: "email", label: "Email", sortable: true, width: "15%" },
  { key: "role", label: "Role", sortable: true, width: "15%" },
  { key: "is_active", label: "Status", sortable: true, width: "10%" },
  { key: "created_at", label: "Created", sortable: true, width: "10%" },
];

const errorMessages = {
  400: "Bad Request. The server could not understand the request.",
  401: "Unauthorized access. Please check your authentication token or log in again.",
  403: "You do not have permission to access this resource. Contact a staff member to assign you the permissions to access this page.",
  404: "Page Not Found.",
  500: "Internal Server Error - Something went wrong on the server.",
};

// Computed
const user = computed(() => useStore.getUser());

const isFiltered = computed(() => searchQuery.value !== "");

const totalPages = computed(() => {
  return itemsPerPage.value === -1
    ? 1
    : Math.ceil(filteredUsers.value.length / itemsPerPage.value);
});

const paginatedUsers = computed(() => {
  if (itemsPerPage.value === -1) {
    return filteredUsers.value.map(d => ({
      ...d,
      status: d.is_online
    }));
  }
  const start = (currentPage.value - 1) * itemsPerPage.value;
  const end = start + itemsPerPage.value;
  return filteredUsers.value.slice(start, end).map(d => ({
    ...d,
    status: d.is_online
  }));
});

const componetObjects = computed(() => {
  return selectedUsers.value.map((user) => ({
    values: [
      { name: "Name", value: user.name },
    ],
  }));
});


const manageRole = (item) => {
  pendingUserID.value = item.id
  changingUser.value = true;
  currentRole.value = item.role;
}

const manageUserStatus = async (item) => {
  pendingUser.value = item
  isTogglingUserStatus.value = true;
  pendingUserID.value = item.id;
};


const activateThisUser = async () => {
  try {
    const response = await activateUser(pendingUserID.value);
    isTogglingUserStatus.value = false;
    await fetchUsers();
  } catch (error) {
    console.error(error)
  }
};


const deactivateThisUser = async () => {
  try {
    const response = await deactivateUser(pendingUserID.value);
    isTogglingUserStatus.value = false;
    await fetchUsers();
  } catch (error) {
    console.error(error)
  }
};


const changeUserRole = async() => {
  const newRoleObject = { role : selectedRole.value }
  try {
    const response = await updateUserRole(pendingUserID.value, newRoleObject)
    changingUser.value = false;
    await fetchUsers();
  } catch (error) {
    console.error(error)
  }
};


const deleleThisUser = async (id) => {
  try {
    const response = await deleteUser(id);
    await fetchUsers();
  } catch (error) {
     console.error(error);
  }
};


const addANewUser = async () => {
  try {
    const response = await addNewUser(newUserObject.value);
    await fetchUsers();
  } catch (error) {
     console.error(error);
  }
};


const handleShowHowToDevice = (value) => {
  showHowToDevice.value = value;
};

const checkPermissions = async () => {
  if (user.value) {
    canAdd.value = await useStore.hasPermission("Can add user");
    canDelete.value = await useStore.hasPermission("Can delete user");
    canView.value = await useStore.hasPermission("Can view user");
  }
};

const fetchUsers = async () => {
  try {
    users.value = await getUsers();
    applyFilters();
  } catch (err) {
    if (err.response) {
      error.value = `${errorMessages[err.response.status]}`;
      errorCode.value = `${err.response.status}`;
    } else {
      error.value = `Unknown Error`;
    }
  }
};

const goToDeviceDetail = (user) => {
  router.push({
    name: "DeviceDetails",
    params: { id: user.id },
  });
};

const applyFilters = () => {
  filteredUsers.value = users.value.filter((user) =>
    user.email.toLowerCase().includes(searchQuery.value.toLowerCase())
  );
  currentPage.value = 1;
};

const updateSelectedUsers = (selectedItems) => {
  selectedUsers.value = selectedItems;
};

const applyBulkAction = async () => {
  if (bulkAction.value === "delete") {
    deletingItem.value = true;
  }
  bulkAction.value = "";
};

const confirmDelete = async () => {
  isDeleting.value = true;
  await deleteSelectedDevices();
  deletingItem.value = false;
  bulkAction.value = "";
};

const deleteSelectedDevices = async () => {
  if (selectedUsers.value.length === 0) {
    alert("No devices selected for deletion");
    return;
  }

  try {
    const deletePromises = selectedUsers.value.map((device) =>
      deleteDevice(device.id)
    );
    await Promise.all(deletePromises);

    users.value = users.value.filter(
      (device) => !selectedUsers.value.some((item) => item.id === device.id)
    );
    table.value?.clearSelection();
    applyFilters();
    selectedUsers.value = [];
  } catch (err) {
    console.error("Error during bulk delete:", err);
    alert("An error occurred while deleting users. Please try again.");
  } finally {
    isDeleting.value = false;
  }
};


const formatValue = (key, value) => {
  if (key === "last_seen" || key === "created_at") {
    return formatDateTime(value);
  }
  return value !== null && value !== undefined ? value : "N/A";
};

const formatDateTime = (dateString) => {
  if (!dateString) return "N/A";
  const options = {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    timeZoneName: "short",
  };
  return new Date(dateString).toLocaleString(undefined, options);
};

const changePage = (delta) => {
  currentPage.value += delta;
};

const updatePagination = () => {
  currentPage.value = 1;
};

// Watchers
watch(searchQuery, () => {
  applyFilters();
});

watch(
  user,
  (newUser) => {
    if (newUser) {
      checkPermissions();
    }
  },
  { immediate: true }
);

// Lifecycle
onMounted(async () => {
  checkPermissions();
  await fetchUsers();
  applyFilters();
});

// Expose methods/refs that might be needed by template ref
defineExpose({
  table,
  handleShowHowToDevice,
  goToDeviceDetail,
  updateSelectedDevices: updateSelectedUsers,
  applyBulkAction,
  confirmDelete,
  formatValue,
  formatDateTime,
  changePage,
  updatePagination,
});
</script>